import { FilterFilled } from "@ant-design/icons";
import { SORT_ORDER } from "@entities/brief/lib/constants";
import { useEditDataFieldV2Data } from "@entities/brief/lib/hooks/useEditDataFieldV2Data";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useGetDynamicFacets } from "@entities/campaigns/lib/hooks";
import {
  CustomFieldType,
  IBriefInfluencerV2,
  IDataFieldValue,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { formatData } from "@shared/lib/utils/formatData";
import { Button, Input } from "@shared/ui";
import NumberRangeFilter from "@shared/ui/filters/NumberRangeFilter";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { ColumnSortUI } from "@shared/ui/filters/ColumnSortUI";
import { Close } from "@shared/ui/icons";
import InputNumber from "@shared/ui/InputNumber";
import { SelectButton } from "@shared/ui/Select";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { getDynamicColumnSizing } from "@widgets/CampaignBrief/lib/getDynamicColumnSizing";
import {
  UploadCell,
  UploadItem,
} from "@widgets/CampaignBrief/ui/CampaignBriefTable/UploadCell";
import { Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styles from "../ui/styles.module.scss";

type SortByState =
  | {
      sortByDynamicFields?: {
        [key: string]: SORT_ORDER;
      };
    }
  | undefined;

type DynamicFieldsState = {
  [key: string]: {
    equalTo?: string[];
    range?: {
      from: number;
      to: number;
    };
  };
};

export type UseClosureDetailCustomFieldColumnsParams = {
  briefId: string;
  briefInfluencers?: IBriefInfluencerV2[];
  sortBy: SortByState;
  setSortBy: React.Dispatch<React.SetStateAction<SortByState>>;
  dynamicFields: DynamicFieldsState;
  setDynamicField: (
    field: string,
    value?: string[] | { from: number; to: number }
  ) => void;
  onFieldEditSuccess: () => void;
};

export function useClosureDetailCustomFieldColumns({
  briefId,
  briefInfluencers = [],
  sortBy,
  setSortBy,
  dynamicFields,
  setDynamicField,
  onFieldEditSuccess,
}: UseClosureDetailCustomFieldColumnsParams): ColumnsType<IBriefInfluencerV2> {
  const { dynamicFacets } = useGetDynamicFacets(briefId || "");
  const { brief } = useGetBrIefById(briefId || "");

  const visibleCustomFields = useMemo(() => {
    const customFields: IFieldConfig[] = [
      ...(brief?.dataFieldsV2?.filter((field) => field.isSpecialField) || []),
    ];

    return customFields;
  }, [brief?.dataFieldsV2]);

  const [editFieldProps, setEditFieldProps] = useState<{
    briefInfluencerId: string;
    key: string;
    value?: IDataFieldValue;
    fieldType?: CustomFieldType;
    originalValue?: IDataFieldValue;
  }>({
    briefInfluencerId: "",
    key: "",
    value: undefined,
    fieldType: undefined,
    originalValue: undefined,
  });

  const { editDataField, isEdited, isEditing } =
    useEditDataFieldV2Data(briefId);

  useEffect(() => {
    if (isEdited) {
      setEditFieldProps({
        briefInfluencerId: "",
        key: "",
        value: undefined,
        fieldType: undefined,
        originalValue: undefined,
      });
    }
  }, [isEdited]);

  return useMemo(
    () =>
      visibleCustomFields.map((field) => {
        const headerText = field.displayName || field.key;
        const columnValues =
          field.type === CustomFieldType.UPLOAD
            ? []
            : briefInfluencers.map((record) => {
                const value = record.dataFields?.[field.key];
                if (field.type === "number")
                  return value ? (formatData(Number(value), "0.0a") as string) : "-";
                if (Array.isArray(value)) return value.join(", ");
                return value ? String(value) : "-";
              });
        const { width: dynamicWidth, limit: dynamicLimit } =
          getDynamicColumnSizing(headerText, columnValues);
        const isEditingColumn = editFieldProps.key === field.key;

        const currentSort = sortBy?.sortByDynamicFields?.[field.key];
        const sortUI = field.type === "number" ? (
          <ColumnSortUI
            sortActive={!!currentSort}
            sortDirection={
              currentSort === SORT_ORDER.ASCEND
                ? "asc"
                : currentSort === SORT_ORDER.DESCEND
                  ? "desc"
                  : undefined
            }
            onSortChange={(dir) => {
              const newSort =
                dir === "asc"
                  ? SORT_ORDER.ASCEND
                  : dir === "desc"
                    ? SORT_ORDER.DESCEND
                    : undefined;
              const newSortByDynamicFields = {
                ...sortBy?.sortByDynamicFields,
              };
              if (newSort === undefined) {
                delete newSortByDynamicFields[field.key];
              } else {
                newSortByDynamicFields[field.key] = newSort;
              }

              setSortBy({
                ...sortBy,
                sortByDynamicFields: newSortByDynamicFields,
              });
            }}
          />
        ) : null;

        return {
          title: (
            <TextWithTooltip
              text={headerText}
              textClassName={styles.metricsHeader}
              limit={dynamicLimit}
            />
          ),
          dataIndex: field.key,
          key: field.key,
          width: isEditingColumn ? Math.max(200, dynamicWidth) : dynamicWidth,
          filterCount:
            (dynamicFields?.[field.key]?.equalTo?.length || 0) +
            (dynamicFields?.[field.key]?.range?.from !== undefined || dynamicFields?.[field.key]?.range?.to !== undefined ? 1 : 0) +
            (sortBy?.sortByDynamicFields?.[field.key] ? 1 : 0),
          filterDropdownProps: {
            destroyPopupOnHide: true,
          },
          filtered: !!(
            dynamicFields?.[field.key]?.equalTo?.length ||
            dynamicFields?.[field.key]?.range?.from !== undefined ||
            dynamicFields?.[field.key]?.range?.to !== undefined ||
            sortBy?.sortByDynamicFields?.[field.key]
          ),
          filterDropdown: (props: { confirm: () => void; close: () => void }) => {
            if (field.type === "string" || field.type === "dropdown") {
              return (
                <SelectFilter
                  title={headerText}
                  props={props}
                  placeholder={` Select ${field.displayName}`}
                  itemsToDisplay={(dynamicFacets?.[field.key] || []).map(
                    (item) => ({
                      name: item._id,
                      value: item._id,
                    })
                  )}
                  selectedItems={dynamicFields?.[field.key]?.equalTo || []}
                  handleSelectionChange={(value) => {
                    setDynamicField(field.key, value);
                  }}
                />
              );
            }

            if (field.type === "number") {
              const currentRange = dynamicFields?.[field.key]?.range || {};

              return (
                <NumberRangeFilter
                  title={headerText}
                  before={sortUI}
                  props={props}
                  placeholder={`Select ${field.displayName} range`}
                  selectedRange={currentRange}
                  handleRangeChange={(range) => {
                    setDynamicField(field.key, {
                      from: range.from || 0,
                      to: range.to || 0,
                    });
                  }}
                />
              );
            }

            return null;
          },
        render: (_: unknown, record: IBriefInfluencerV2) => {
          const value = record.dataFields?.[field.key];
          const isEditingField =
            editFieldProps.key === field.key &&
            record._id === editFieldProps.briefInfluencerId;

          if (isEditingField) {
            const handleSave = () => {
              editDataField({
                briefInfluencerId: record._id,
                dataField: {
                  [editFieldProps.key]: editFieldProps.value || "",
                },
                handleSuccess: () => {
                  onFieldEditSuccess();
                },
              });
            };

            const handleCancel = () => {
              setEditFieldProps({
                briefInfluencerId: "",
                key: "",
                value: undefined,
                fieldType: undefined,
                originalValue: undefined,
              });
            };

            const suffixActions = (
              <Space size={2}>
                <Button
                  icon={<Check size={10} />}
                  type="text"
                  size="small"
                  loading={isEditing}
                  onClick={handleSave}
                  className={styles.editActionButton}
                />
                <Button
                  icon={<Close size={10} />}
                  type="text"
                  size="small"
                  onClick={handleCancel}
                  className={styles.editActionButton}
                />
              </Space>
            );

            if (field.type === CustomFieldType.DROPDOWN) {
              const dropdownOptions =
                field.dropdownOptions?.map((opt) => ({
                  label: opt,
                  value: opt,
                })) || [];
              const currentValue = Array.isArray(editFieldProps.value)
                ? editFieldProps.value
                : editFieldProps.value
                  ? [String(editFieldProps.value)]
                  : [];

              return (
                <SelectButton
                  mode="multiple"
                  value={currentValue}
                  onChange={(val) => {
                    const newValue = Array.isArray(val) ? val : [];
                    const originalValue = editFieldProps.originalValue;
                    const wasOriginalEmpty =
                      !originalValue ||
                      (Array.isArray(originalValue) &&
                        originalValue.length === 0) ||
                      originalValue === "";

                    if (newValue.length === 0 && !wasOriginalEmpty) {
                      handleCancel();
                    } else {
                      editDataField({
                        briefInfluencerId: record._id,
                        dataField: {
                          [editFieldProps.key]: newValue,
                        },
                        handleSuccess: () => {
                          onFieldEditSuccess();
                        },
                      });
                    }
                  }}
                  options={dropdownOptions}
                  placeholder="Select..."
                  size="small"
                  withFooterActions
                  applyText="Apply"
                  clearText="Cancel"
                  className={styles.customFieldSelect}
                />
              );
            }

            if (field.type === "number") {
              return (
                <InputNumber
                  value={
                    typeof editFieldProps.value === "number"
                      ? editFieldProps.value
                      : editFieldProps.value
                        ? Number(editFieldProps.value)
                        : undefined
                  }
                  onChange={(val) => {
                    setEditFieldProps({
                      ...editFieldProps,
                      value: val || 0,
                    });
                  }}
                  onPressEnter={handleSave}
                  size="small"
                  className={styles.customFieldInput}
                  addonAfter={suffixActions}
                  autoFocus
                />
              );
            }

            return (
              <Input
                value={String(editFieldProps.value || "")}
                onChange={(e) => {
                  setEditFieldProps({
                    ...editFieldProps,
                    value: e.target.value,
                  });
                }}
                onPressEnter={handleSave}
                size="small"
                className={styles.customFieldInput}
                suffix={suffixActions}
                autoFocus
              />
            );
          }

          if (field.type === CustomFieldType.UPLOAD) {
            const uploadItems: UploadItem[] =
              Array.isArray(value) &&
              value.every(
                (item) =>
                  typeof item === "object" &&
                  item !== null &&
                  "url" in item &&
                  "type" in item
              )
                ? (value as UploadItem[])
                : [];

            return (
              <UploadCell
                fieldKey={field.key}
                briefInfluencerId={record._id}
                value={uploadItems}
                onChange={(newItems) => {
                  editDataField({
                    briefInfluencerId: record._id,
                    dataField: {
                      [field.key]: newItems,
                    },
                    handleSuccess: () => {
                      onFieldEditSuccess();
                    },
                  });
                }}
              />
            );
          }

          const displayValue: string =
            field.type === "number"
              ? value
                ? (formatData(Number(value), "0.0a") as string)
                : "-"
              : Array.isArray(value)
                ? value.join(", ")
                : value
                  ? String(value)
                  : "-";

          return (
            <div
              className={styles.editableCell}
              onClick={(e) => {
                e.stopPropagation();
                setEditFieldProps({
                  briefInfluencerId: record._id,
                  key: field.key,
                  value: value as string | number | string[] | undefined,
                  fieldType: field.type as CustomFieldType,
                  originalValue: value as
                    | string
                    | number
                    | string[]
                    | undefined,
                });
              }}
            >
              <TextWithTooltip
                text={displayValue}
                limit={dynamicLimit}
                textClassName={styles.customFieldValue}
              />
            </div>
          );
        },
      };
      }),
    [
      visibleCustomFields,
      briefInfluencers,
      editFieldProps,
      sortBy,
      setSortBy,
      dynamicFields,
      setDynamicField,
      dynamicFacets,
      editDataField,
      isEditing,
      onFieldEditSuccess,
    ]
  );
}
